"use client";

import { useMemo } from "react";

import { useWidgetAggregateSeries } from "@/hooks/useWidgetAggregateSeries";
import { useWidgetSamples } from "@/hooks/useWidgetData";
import { buildCreateAlertFromWidgetHref } from "@/modules/alerts/utils/create-alert-from-widget";
import { formatBooleanState, formatTelemetryValue } from "@/modules/telemetry/page-utils";

import { WidgetChrome } from "./WidgetChrome";
import type { WidgetShellProps } from "./types";

export function StatWidget({ widget, config, timeRange, customRange, hasActiveAlert }: WidgetShellProps) {
  const seriesKey = config.series_key;
  const channelKey = config.channel ?? "";

  const { data, isLoading, error } = useWidgetAggregateSeries(widget.id, widget.type, {
    deviceId: config.device_id,
    channel: seriesKey,
    timeRange,
    customRange,
    aggregation: config.aggregation,
    refreshIntervalSeconds: config.refresh_interval_seconds,
  });
  const rawQuery = useWidgetSamples({
    deviceId: config.device_id,
    channel: channelKey,
    timeRange,
    customRange,
    aggregation: config.aggregation,
    refreshIntervalSeconds: config.refresh_interval_seconds,
    enabled: Boolean(channelKey),
  });

  const numericValue = useMemo(() => {
    const pts = data?.points ?? [];
    for (let i = pts.length - 1; i >= 0; i -= 1) {
      const value = pts[i]!.values[seriesKey];
      if (value != null && typeof value === "number" && !Number.isNaN(value)) {
        return value;
      }
    }
    return null;
  }, [data, seriesKey]);

  const rawValue = useMemo(() => {
    if (!channelKey) {
      return null;
    }
    const lastRow = rawQuery.rows[rawQuery.rows.length - 1];
    return lastRow?.data?.[channelKey] ?? null;
  }, [channelKey, rawQuery.rows]);

  const displayValue = useMemo(() => {
    if (numericValue != null) {
      return numericValue.toLocaleString(undefined, { maximumFractionDigits: 3 });
    }
    if (typeof rawValue === "boolean") {
      return formatBooleanState(rawValue);
    }
    if (rawValue != null) {
      return formatTelemetryValue(rawValue);
    }
    return "—";
  }, [numericValue, rawValue]);

  const title =
    config.title?.trim() || config.formula?.trim() || config.metric_id?.slice(0, 8) || config.channel || "Stat";
  const effectiveError = error ?? rawQuery.error;

  return (
    <WidgetChrome
      widget={widget}
      title={title}
      subtitle="Stat · latest reading"
      isLoading={(isLoading && !data) || (rawQuery.isLoading && rawQuery.rows.length === 0)}
      error={effectiveError}
      createAlertHref={buildCreateAlertFromWidgetHref(config)}
      hasActiveAlert={hasActiveAlert}
    >
      {!effectiveError && !isLoading && !rawQuery.isLoading && numericValue == null && rawValue == null ? (
        <p className="text-caption text-muted">No samples in this window.</p>
      ) : !effectiveError ? (
        <p className="text-3xl font-semibold tabular-nums tracking-tight text-foreground md:text-4xl">
          {displayValue}
        </p>
      ) : null}
    </WidgetChrome>
  );
}
